/* Carewell-style option selector, per owner's request — replaces the
   plain "Choose your Option" dropdown's visual presentation with:
   1) a closed-state trigger showing the currently selected option's
      name, its SKU underneath, and "See all N options" below that.
   2) clicking it opens a full-screen popup listing every option as a
      row (name, SKU, price), any of which can be clicked to select it.

   The stock <custom-select> markup stays fully intact in the DOM
   (hidden via CSS, not removed) — this only reads its existing
   .custom-select__option list items (which already carry
   data-value/data-variant-id from Shopify's own rendering) and
   forwards clicks to them, reusing 100% of Shopify's real
   variant-switching/price-update logic rather than reimplementing it.
   SKU per variant isn't available on those list items, so it's read
   separately from a small JSON lookup rendered alongside it in
   snippets/variant-picker.liquid.

   Real gap found: products with more than one option (e.g. Color +
   Corner Style + Thickness on a mattress cover) have one
   .option-selector per option — the original version here built one
   trigger+popup PER option-selector independently, so a 3-option
   product showed 3 separate stacked "See all N options" boxes, each
   only covering one dimension. Owner asked for a single combined
   entry point instead. Fixed by grouping option-selectors and
   building ONE trigger + ONE popup with a labelled section per option
   instead of one popup each. Single-option products (the common
   case, e.g. BetterDry's pack-size options) are unaffected — same
   one trigger/one flat list as before.

   Second, much deeper real bug found after that: owner reported a
   selected chip staying highlighted after picking a new one, and
   picking Style/Height after Color reverting back to whatever color
   was selected on page load. Root cause, confirmed directly: Shopify's
   own variant-picker.js does a full AJAX re-render of the ENTIRE
   variant-picker section on every option change — not just the
   option group that changed. Confirmed live (comparing object
   identity before/after a Height change): the Color group's own
   .option-selector, its <custom-select>, and even the <variant-picker>
   element itself all get replaced with brand new DOM nodes carrying
   freshly recomputed data-variant-id values. This project's own
   trigger/popup UI was built once and kept references to the
   ORIGINAL option/custom-select nodes — the instant any option
   changed, those references pointed at detached, stale markup,
   explaining both the frozen combined-variant summary bug (fixed
   earlier) and now this: clicking a Style/Height chip called
   selectOption() on a stale <custom-select> using a stale option
   whose data-variant-id still encoded the OLD (page-load) color.
   Also confirmed the earlier "ready" flag (set as a dataset property
   on the .option-selector itself) gets wiped every time too, since
   the flagged node is the one being replaced — meaning scan() kept
   re-detecting "new" unflagged option-selectors after every change
   and building ANOTHER duplicate trigger+popup (confirmed: 2 stray
   .llm-option-popup-overlay elements already existed in the DOM after
   a single option change in testing).

   Fixed by never caching option/custom-select DOM references past
   their immediate use — every read and every click re-resolves the
   CURRENT live nodes by their content (data-option name, data-value)
   rather than by object identity, scoped to the nearest
   `[id^="shopify-section-"]` wrapper (confirmed stable across these
   re-renders, unlike <variant-picker> or .option-selector). The
   "already built" flag now lives on that same stable wrapper instead
   of on the option-selector, so scan() no longer rebuilds duplicate
   UI after every option change.

   Third real bug, found via the quick-add drawer on a product page
   (not a collection/search page): the SKU/price JSON blocks
   (#llm-variant-sku-data / #llm-variant-price-data, rendered in
   snippets/variant-picker.liquid) use plain ids, but a product page
   already has its own copy of these for the main product — opening
   quick-add for a DIFFERENT product (e.g. from "You may also like")
   injects a second, duplicate-id copy for that product. A single,
   shared, page-wide skuMap/priceMap read via document.getElementById
   always resolves to the FIRST matching id in the document — the
   main product's own data, never the drawer's — so the popup for the
   second product showed every option correctly (that part reads live
   DOM nodes, unaffected) but with blank SKU/price text, since its
   variant ids were never keys in the wrong map. Confirmed directly:
   two #llm-variant-sku-data elements existed on the page, and the one
   document.getElementById returned belonged to the main product, not
   the one the drawer was showing. Fixed by scoping the sku/price
   lookup to each popup's own sectionRoot (searching sectionRoot itself
   for the shopify-section case, or its parent for the drawer case,
   where the data blocks are rendered as siblings of </variant-picker>
   rather than descendants of it) instead of a single shared,
   page-wide map — every function that needs sku/price data now lives
   inside buildGroup() itself so it closes over that one call's own,
   correctly-scoped maps rather than a shared outer variable. */
document.addEventListener('DOMContentLoaded', function () {
  /* Prices in the variant picker stay FLAT — no superscript cents (owner 2026-09-05: Amazon uses
     them "just in main pricing spots. Not on the variant cards"). This was briefly superscripted
     on 2026-09-04 and pulled back.

     Kept as a single helper rather than reverting to bare textContent at each of the eight sinks,
     so the decision lives in one place: if the picker should ever match the primary price again,
     this is the only function to change. */
  function setMoney(el, s) {
    el.textContent = s || '';
  }

  function optionValueOf(opt) {
    return opt.getAttribute('data-value') || opt.textContent.trim();
  }

  // Per-variant inventory + thresholds — same JSON the buy-box stock badge uses
  // (#llm-variant-inventory-data = { "<variantId>": {q, t}, ... }, #llm-inventory-config = {veryLow, low}).
  // Used to flag low-stock (but still in-stock) variant tiles with a "Low stock" line.
  // cardLow is the tile-specific threshold, SEPARATE from veryLow/low which drive the buy-box
  // badge + standalone stock badge — owner 2026-07-30 wanted the option cards to flag "Low stock"
  // only at <= 3, without changing the buy-box messaging (which stays on veryLow=6).
  var llmInvData = {}, llmInvCfg = { veryLow: 8, low: 20, cardLow: 3 };
  try {
    var _invEl = document.getElementById('llm-variant-inventory-data');
    if (_invEl) llmInvData = JSON.parse(_invEl.textContent || '{}');
    var _cfgEl = document.getElementById('llm-inventory-config');
    if (_cfgEl) {
      var _c = JSON.parse(_cfgEl.textContent || '{}');
      if (_c.veryLow) llmInvCfg.veryLow = _c.veryLow;
      if (_c.low) llmInvCfg.low = _c.low;
      if (_c.cardLow) llmInvCfg.cardLow = _c.cardLow;
    }
  } catch (e) {}
  function llmIsLowStock(variantId) {
    var d = variantId ? llmInvData[String(variantId)] : null;
    // Flag the option card only when stock is <= cardLow (3). This is intentionally tighter than
    // the buy box's veryLow (6) — owner: the cards should warn only on genuinely-scarce variants.
    return !!(d && d.t && d.q > 0 && d.q <= llmInvCfg.cardLow);
  }

  // Finds the #llm-variant-sku-data/#llm-variant-price-data belonging
  // to THIS sectionRoot specifically, not just whichever one happens
  // to be first in the document. The data blocks are rendered as
  // siblings immediately after </variant-picker> in
  // snippets/variant-picker.liquid, so they're descendants of a real
  // `[id^="shopify-section-"]` wrapper (search within sectionRoot
  // itself) but plain siblings of the bare <variant-picker> fallback
  // used inside the quick-add drawer (search within its parent).
  function readScopedDataMap(sectionRoot, id) {
    const scope = sectionRoot.querySelector('#' + id) ? sectionRoot : sectionRoot.parentElement;
    const el = scope ? scope.querySelector('#' + id) : null;
    if (!el) return {};
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return {};
    }
  }

  // Re-resolves the CURRENT live .option-selector for a given option
  // name (e.g. "Color"), scoped to sectionRoot. Never cache the
  // result past a single synchronous use — see file header.
  function findLiveSelector(sectionRoot, optionName) {
    const selectors = sectionRoot.querySelectorAll('.option-selector[data-selector-type="dropdown"]');
    for (let i = 0; i < selectors.length; i++) {
      if (selectors[i].getAttribute('data-option') === optionName) return selectors[i];
    }
    return null;
  }

  // Re-resolves the CURRENT live option + its <custom-select> for a
  // given (option name, option value) pair, e.g. ("Height", "16 inch
  // (+$20.00)"). This is the one function every click handler and
  // every refresh routine goes through — nothing else in this file
  // is allowed to hold onto an option/custom-select node across an
  // event boundary.
  function findLiveOption(sectionRoot, optionName, optionValue) {
    const selector = findLiveSelector(sectionRoot, optionName);
    if (!selector) return null;
    const opts = selector.querySelectorAll('.custom-select__option');
    for (let i = 0; i < opts.length; i++) {
      if (optionValueOf(opts[i]) === optionValue) {
        return { opt: opts[i], customSelectEl: selector.querySelector('custom-select') };
      }
    }
    return null;
  }

  // optionNames: e.g. ["Color", "Style", "Height"], in display order.
  // sectionRoot: the stable `[id^="shopify-section-"]` wrapper (or
  // best-available fallback) — never replaced by Shopify's own
  // re-renders, so this is the one thing safe to hold onto.
  /* ===== SPLIT PICKER — the Size / Color / Package button rows (see the long note in buildGroup).
     File scope since 2026-10-27 so the Frequently-bought-together popup (llm-fbt.js) draws the very
     same rows through window.llmSplit instead of a private copy that would drift. */
  const SPLIT_SITEWIDE = true;
  const splitParts = function (v) { return String(v).split('|').map(function (p) { return p.replace(/\s+/g, ' ').trim(); }); };
  const splitWanted = function (values) {
    const qs = new URLSearchParams(location.search).get('split');
    if (qs === '0') return false;
    const on = qs === '1' || SPLIT_SITEWIDE;
    /* Every product with a choice to make, one attribute or several (owner 2026-10-26: "make 1
       option products the same style as 2 and 3 option products"). A one-part product
       ("Pack of 14" / "Case of 56") becomes a single row of buttons. The only products that keep
       the old cards are those whose values do not all split into the same number of parts. */
    if (!on || values.length < 2) return false;
    const n = splitParts(values[0]).length;
    return values.every(function (v) { return splitParts(v).length === n; });
  };
  const PACK_RE = /\b(pack|case|box|bag|carton|trial|single|count|each|sample|ct|bottle|tube|jar|tub|roll|vial|canister|dispenser|packet|pouch)s? of\b|\b(pack|case|box|bag|carton|trial|single|count|each|sample|ct|pair)\b/i;
  const SIZE_RE = /^(X{0,3}S|SM|MD|LG|X{0,3}L|\d?XL|\dX|S|M|L|small|medium|large|regular|youth|petite|bariatric|one size)\b|\d+\s*("|in\b|inch)/i;
  const COLOR_SWATCH = { white: '#ffffff', blue: '#3a6fd8', pink: '#f4a6c6', black: '#1f1f1f', purple: '#7d4cc4',
    red: '#d93a3a', green: '#3c9a52', grey: '#9aa3ad', gray: '#9aa3ad', beige: '#e3d3b4', tan: '#d2b48c',
    navy: '#1d2f5a', yellow: '#f2cf3a', orange: '#f08a2c', clear: '#eef3f7', sapphire: '#1f4fa8',
    // Named product colours (owner 2026-10-26, Gary plastic pants). Multi-word names are matched
    // before single words (longest key first, below), so "Soft Pastel Blue" is not plain blue.
    'pastel blue': '#bcd8f5', 'pastel pink': '#f9d3e2', 'pastel lemon': '#fbf1b8',
    'milky white': '#fbf6e4', softwear: '#d9dce0',
    euroflex: '#eceef1',   // opaque clear vinyl: a shade lighter than softwear (owner 2026-10-27)
    'tie-dye': 'conic-gradient(#f06292, #ffb74d, #fff176, #81c784, #64b5f6, #ba68c8, #f06292)' };
  /* Row heading for attribute d of n. Measured against all 637 products this mode would take
     over (scratchpad split_scope.mjs, 2026-10-26): the first version labelled 115 rows "Option"
     and 11 rows "Package" twice, because ANY "Sample" or "100 ct" in a size list read as a pack.
     Now: the LAST attribute is always the package; earlier ones test by MAJORITY, most specific
     first, so one odd value cannot rename the row. */
  const SCENT_RE = /\b(scent|scented|unscented|fragrance|fragranced|odor)/i;
  const FLAVOR_RE = /\b(vanilla|chocolate|strawberr|berry|mocha|coffee|caramel|banana|lemon|lime|orange|peach|cherry|grape|apple|mango|tropical|mint|unflavou?red|flavou?r|cream|fudge|butter pecan|dutch|cafe)/i;
  const FLANGE_RE = /flange|\b\d+(\.\d+)?\s*mm\b|\b[\d¼½¾]+\s*(inch|in\.?)\b(?!.*(oz|lb))/i;
  const MEASURE_RE = /\b\d+(\.\d+)?\s*(oz|fl\.? ?oz|ml|l|liter|litre|gram|g|kg|lb|lbs|cc|gal|gallon|fr\.?|french|square inch|sq\.? ?in)(\b|$)|\b\d+T\s*-\s*\d+T\b|^size\b|^x{1,3}-?(small|large)\b|\d'\s*x\s*\d/i;
  const COMPRESSION_RE = /mm\s*hg/i;
  const SHADE_RE = /^(light|medium|dark|fair|deep|tan|natural)$/i;
  const COUNT_RE = /^\s*[\d,]+\s*(ct|count)\b/i;
  const TYPE_RE = /\b(convex|flat|coupling|closed|drainable|cut[- ]to[- ]fit|pre[- ]cut|moldable|regular|extra|plus|standard)\b/i;
  const dimLabel = function (vals, d, n) {
    const most = function (re) { return vals.filter(function (x) { return re.test(x); }).length >= Math.max(1, vals.length / 2); };
    // The last attribute is normally the package, but some products end on the size instead
    // (garywear diaper covers: "Navy | XS (25\" to 35\" waist…)").
    // ...and some end on a colour/material (gary-pull-on-plastic-pants: "Milky White (4 gauge)"),
    // which read "Package:" until 2026-10-26. So the last row is "Package" only when it looks like
    // one; otherwise it goes through the same tests as any other row.
    const isLast = d === n - 1;
    /* Order matters: most specific first. One-row products (2026-10-26) added ~3,600 products to
       this, so the checks below were re-run against every active product (split_scope.mjs). */
    if (isLast && most(PACK_RE)) return 'Package';
    if (most(/^\$\s?\d/)) return 'Amount';                                  // gift card
    if (most(/^(left|right)\b/i)) return 'Side';                            // wrist braces, splints
    if (most(/\bconsistency\b|\b(nectar|honey|pudding)\b/i)) return 'Consistency';  // thickeners
    if (most(/\bgauge\b/i) && !most(/\b(white|blue|pink|softwear|sapphire|lemon|carousel)\b/i)) return 'Gauge';  // lancets, not "Milky White (4 gauge)"
    if (most(/\b(wide|width)\b/i)) return 'Width';          // abdominal binder: 9" Wide / 12" Wide
    if (most(/\bcoupling\b/i)) return 'Coupling';           // ostomy: Red / Green / Blue Coupling
    if (most(SIZE_RE)) return 'Size';
    if (most(SCENT_RE)) return 'Scent';                     // "Freshly Scented 33.8 oz": the scent is the choice
    if (most(MEASURE_RE)) return 'Size';                    // "4 fl oz, Clear Amber" is a size, not a colour
    // Majority of values name a colour ("Soft Pastel Blue" counts, not just exact swatch names).
    if (most({ test: function (x) { return !!COLOR_SWATCH[x.toLowerCase()] || /\b(white|blue|pink|black|purple|red|green|gr[ae]y|beige|tan|navy|yellow|orange|clear|tie-dye|print|camo|sapphire|softwear|lavender|peach|silver|chrome|gold|bronze|teal|burgundy)\b/i.test(x); } })) return 'Color';
    if (most(/\bservings?\b/i)) return 'Servings';          // Benefiber: 38 / 62 / 125 Servings
    if (most(FLAVOR_RE)) return 'Flavor';
    if (most(FLANGE_RE)) return 'Flange';
    if (most(COMPRESSION_RE)) return 'Compression';
    if (most(COUNT_RE)) return 'Count';
    if (most(SHADE_RE)) return 'Shade';
    if (most(TYPE_RE)) return 'Type';
    if (most(PACK_RE)) return 'Pack';
    return 'Type';            // Pouch/Tray, Bottle/Packets, With/Without Backrest... read fine as "Type"
  };
  const MADE_RE = /\s*\*?\s*made in (the )?usa\s*\*?\s*/i;

  /* rows: [{ parts, oos, vid }] — one per variant. selRow: the current one. choose(row) selects it.
     names: the row headings when the product has real Shopify option names (separate options);
     null to work them out from the values (combined "A | B" options). */
  function renderSplitInto(container, rows, selRow, choose, names, valueMap) {
    container.textContent = '';
    const n = rows[0].parts.length;
    /* ROW ORDER (owner 2026-10-26): "package should always be last" and "size and then color".
       Products write their parts in different orders (MEGAMAX "Color | Size | Pack", Gary pants
       "Size | Color"), so the rows are reordered here; the product data is untouched and every
       other row keeps its place. Labels are worked out on the ORIGINAL positions (the last part
       is special to dimLabel), then travel with their columns. */
    const origLabels = [];
    for (let i = 0; i < n; i++) {
      const ord = [];
      rows.forEach(function (r) { if (ord.indexOf(r.parts[i]) === -1) ord.push(r.parts[i]); });
      origLabels.push(names ? names[i] : dimLabel(ord, i, n));
    }
    const perm = [];
    for (let i = 0; i < n; i++) perm.push(i);
    const isColor = function (l) { return /^colou?r\b/i.test(l); };   // "Color/Material" too
    const sIdx = origLabels.indexOf('Size'), cIdx = origLabels.findIndex(isColor);
    if (sIdx > -1 && cIdx > -1 && sIdx > cIdx) { perm.splice(perm.indexOf(sIdx), 1); perm.splice(perm.indexOf(cIdx), 0, sIdx); }
    const pIdx = origLabels.indexOf('Package');
    if (pIdx > -1 && perm[n - 1] !== pIdx) { perm.splice(perm.indexOf(pIdx), 1); perm.push(pIdx); }
    rows.forEach(function (r) { r.parts = perm.map(function (i) { return r.parts[i]; }); });
    const rowLabels = perm.map(function (i) { return origLabels[i]; });
    const sel = selRow || rows[0];
    const prefixOk = function (r, upto, parts) {
      for (let i = 0; i < upto; i++) if (r.parts[i] !== parts[i]) return false;
      return true;
    };

    /* Every attribute is a button row, the package included (owner 2026-10-26: "make the package
       cards the same as the color and size with no price and the same selected style"). The price
       is already shown, large, in the buy box for whatever is selected. */
    for (let d = 0; d < n; d++) {
      // values of this attribute, first-seen order, limited to the choices made above it
      const inScope = rows.filter(function (r) { return prefixOk(r, d, sel.parts); });
      const order = [];
      rows.forEach(function (r) { if (order.indexOf(r.parts[d]) === -1) order.push(r.parts[d]); });
      const label = rowLabels[d];
      const group = document.createElement('div');
      group.className = 'llm-split__group';
      const head = document.createElement('div');
      head.className = 'llm-split__label';
      head.innerHTML = '<span class="llm-split__label-name"></span> <span class="llm-split__label-value"></span>';
      head.firstChild.textContent = label + ':';
      head.lastChild.textContent = sel.parts[d].replace(MADE_RE, ' ').trim();
      group.appendChild(head);
      const chips = document.createElement('div');
      chips.className = 'llm-split__chips';
      chips.setAttribute('role', 'radiogroup');
      chips.setAttribute('aria-label', label);
      order.forEach(function (val) {
        const avail = inScope.filter(function (r) { return r.parts[d] === val; });
        // Package row: only packages that exist for this colour + size. Package NAMES differ by
        // size ("Pack of 12" / "Pack of 10" / "Pack of 8", with and without "*MADE IN USA*"), so
        // greying the rest left 7 dead buttons out of 10 (measured on MEGAMAX).
        if (label === 'Package' && !avail.length) return;
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'llm-split__chip';
        b.setAttribute('role', 'radio');
        const on = sel.parts[d] === val;
        b.setAttribute('aria-checked', on ? 'true' : 'false');
        if (on) b.classList.add('is-selected');
        /* Not in the current combination, but it EXISTS elsewhere (Blue at XS on MEGAMAX: Blue only
           comes SM-XL). Since size moved first (2026-10-26) these must not be dead ends — the shopper
           who wants Blue should not have to guess which size has it. Faded but clickable; the click
           moves the earlier rows to the NEAREST combination that has it (see the handler). Only a
           value that exists nowhere at all is disabled. */
        const anywhere = rows.filter(function (r) { return r.parts[d] === val; });
        if (!avail.length) {
          b.classList.add('is-unavailable');
          if (!anywhere.length) b.disabled = true;
          b.title = 'Not available in ' + sel.parts.slice(0, d).join(', ') + ' — click to switch to one that has it';
        }
        /* Out of stock looks only at the rows ABOVE: a size says "Out of stock" only when it is sold
           out in every colour. Tried judging it against the colour picked below as well (2026-10-27)
           and reverted the same day at the owner's word — a size the shopper can still buy in another
           colour should not read as sold out. */
        const oosHere = avail.length > 0 && avail.every(function (r) { return r.oos; });
        if (oosHere) b.classList.add('is-oos');
        if (isColor(label)) {
          // Exact name first, else the colour word inside a longer name ("Milky White (4 gauge)" ->
          // white, "Soft Pastel Blue" -> blue). No match -> no dot, just the text.
          let sw = COLOR_SWATCH[val.toLowerCase()];
          if (!sw) {
            const k = Object.keys(COLOR_SWATCH).sort(function (a, b) { return b.length - a.length; })
              .find(function (c) { return new RegExp('\\b' + c + '\\b', 'i').test(val); });
            if (k) sw = COLOR_SWATCH[k];
          }
          if (sw) { const dot = document.createElement('span'); dot.className = 'llm-split__swatch'; dot.style.background = sw; b.appendChild(dot); }
          // Name in its own column so an "Out of stock" line can sit under it, beside the dot.
          const txt = document.createElement('span'); txt.className = 'llm-split__chip-text';
          const nmEl = document.createElement('span'); nmEl.textContent = val; txt.appendChild(nmEl);
          b.appendChild(txt);
        } else if (label === 'Size') {
          // Closing ")" optional: gary-pull-on-plastic-pants has 'MD (24" to 44"' with it missing,
          // and MD alone showed the raw text while every other size split cleanly.
          const m = val.match(/^([^(]+?)\s*\((.+?)\)?\s*$/);
          const main = document.createElement('span'); main.className = 'llm-split__chip-main'; main.textContent = m ? m[1] : val;
          b.appendChild(main);
          if (m) { const sub = document.createElement('span'); sub.className = 'llm-split__chip-sub'; sub.textContent = m[2].replace(/\s+to\s+/gi, '–'); b.appendChild(sub); }
        } else {
          // Package: "*MADE IN USA*" comes out of the name and sits under it, like a size's range.
          const main = document.createElement('span'); main.className = 'llm-split__chip-main'; main.textContent = val.replace(MADE_RE, ' ').trim();
          /* The bracketed part ("(4 packs)", "(2 count)") always starts its own line (owner 2026-11-02:
             "put those all on the 2nd line. Looks cleaner"), instead of breaking wherever the button
             width happens to fall: "Case of 88" / "(4 packs)", "Sample" / "(2 count)". */
          const pm = main.textContent.match(/^(.+?)\s+(\(.+\))$/);
          if (pm) {
            main.textContent = pm[1];
            const q = document.createElement('span'); q.className = 'llm-split__chip-qty'; q.textContent = pm[2];
            main.appendChild(q);
          }
          b.appendChild(main);
          if (MADE_RE.test(val)) { const sub = document.createElement('span'); sub.className = 'llm-split__chip-sub'; sub.textContent = 'Made in USA'; b.appendChild(sub); }
          /* BEST VALUE back on the package (owner 2026-10-26: "put 'Best Value' back on the package
             options"). Same flag and same pill as the old cards: valueMap decides (per size, samples
             excluded, minimum saving — snippets/llm-variant-value-data.liquid); this only draws it. */
          if (label === 'Package' && avail.length === 1) {
            const vid = avail[0].vid;
            const info = vid && valueMap[vid];
            /* Per-unit price on each package, like Chewy's size buttons (owner 2026-10-28). Same source
               and same rule as the old tiles' unitTextFor(): the label-derived count, never Shopify's
               unit_price (the pack price on ~150 variants), and nothing for a Single (count 1). */
            if (info && info.per && info.count > 1) {
              const u = document.createElement('span');
              u.className = 'llm-split__chip-sub llm-split__chip-unit';
              setMoney(u, '(' + info.per + ' / ea)');
              b.appendChild(u);
            }
            if (info && info.best) {
              b.classList.add('has-best-pill');
              const pill = document.createElement('span');
              pill.className = 'llm-variant-tile__pill';
              pill.textContent = 'Best value';
              pill.setAttribute('aria-hidden', 'true');
              b.appendChild(pill);
            }
          }
        }
        /* "Out of stock" on EVERY row, not just the package (owner 2026-10-26: "soft pastel blue is
           out of stock for XL but it's just lightly greyed out"). On a colour or size it means every
           option under it, given the choices above, is out of stock. Still selectable — the buy box
           then offers back-in-stock alerts as it does for any sold-out variant. */
        if (avail.length && oosHere) {
          const o = document.createElement('span'); o.className = 'llm-split__chip-sub llm-split__chip-oos'; o.textContent = 'Out of stock';
          (b.querySelector('.llm-split__chip-text') || b).appendChild(o);
        }
        b.addEventListener('click', function () {
          if (on || !anywhere.length) return;
          const want = sel.parts.slice(); want[d] = val;
          if (!avail.length) {
            /* Cross-combination jump: among everything with this value, take the one whose EARLIER
               choices are closest to the current ones (by position in each row: XS -> SM, not -> XL),
               then the most later choices kept, then in stock. */
            const pos = function (i, v) { const o = []; rows.forEach(function (r) { if (o.indexOf(r.parts[i]) === -1) o.push(r.parts[i]); }); return o.indexOf(v); };
            const nm = function (x) { return String(x).replace(MADE_RE, ' ').trim().toLowerCase(); };
            let pick = null, pickKey = null;
            anywhere.forEach(function (r) {
              let dist = 0, keep = 0;
              for (let i = 0; i < d; i++) dist += Math.abs(pos(i, r.parts[i]) - pos(i, sel.parts[i])) * Math.pow(100, d - i);
              for (let i = d + 1; i < n; i++) if (nm(r.parts[i]) === nm(want[i])) keep += Math.pow(2, n - i);
              const key = [dist, -keep, r.oos ? 1 : 0];
              if (!pickKey || key[0] < pickKey[0] || (key[0] === pickKey[0] && (key[1] < pickKey[1] || (key[1] === pickKey[1] && key[2] < pickKey[2])))) { pick = r; pickKey = key; }
            });
            choose(pick);
            return;
          }
          /* Keep as much of the shopper's other choices as exists: score each candidate by how
             many LATER attributes still match (compared without "*MADE IN USA*", which differs
             between sizes of the same pack), earlier attributes weighted higher; in stock breaks
             ties. Tie-Dye SM -> White now lands on White SM, not on the first White (XS). */
          const norm = function (x) { return String(x).replace(MADE_RE, " ").trim().toLowerCase(); };
          let best = null, bestScore = -1;
          avail.forEach(function (r) {
            let sc = 0;
            for (let i = d + 1; i < n; i++) if (norm(r.parts[i]) === norm(want[i])) sc += Math.pow(2, n - i);
            sc = sc * 2 + (r.oos ? 0 : 1);
            if (sc > bestScore) { bestScore = sc; best = r; }
          });
          choose(best);
        });
        chips.appendChild(b);
      });
      group.appendChild(chips);
      container.appendChild(group);
    }
  }
  window.llmSplit = { parts: splitParts, wanted: splitWanted, render: renderSplitInto };

  function buildGroup(optionNames, sectionRoot, firstOptionSelector) {
    sectionRoot.dataset.llmPopupBuilt = 'true';

    // Scoped to THIS sectionRoot only — see file header for why this
    // can't be a single shared map read once at the top of the file.
    let skuMap = readScopedDataMap(sectionRoot, 'llm-variant-sku-data');
    let priceMap = readScopedDataMap(sectionRoot, 'llm-variant-price-data');
    let unitPriceMap = readScopedDataMap(sectionRoot, 'llm-variant-unit-price-data');
    /* Per-unit economics computed from the variant LABEL — see snippets/llm-variant-value-data.
       Preferred over unitPriceMap, which carries Shopify's unit_price and is the PACK price on the
       146-of-2,886 variants whose quantity_value is 1. */
    let valueMap = readScopedDataMap(sectionRoot, 'llm-variant-value-data');

    /* ONE place decides what any surface says about per-unit price, so the tiles and the popup rows
       can never disagree about the same variant. Returns '' when nothing trustworthy is known —
       showing no per-unit at all beats showing a pack price wearing an "/ea". */
    function unitTextFor(variantId) {
      const v = variantId && valueMap[variantId];
      /* A Single carries count 1 so it can serve as the BASELINE the case's saving is measured
         against (see the snippet) — but "$19.99" with "($19.99 / ea)" beneath it is the same
         number twice, so that figure stays in the data and out of the UI. Falling through to
         unitPriceMap would reintroduce it, so a known count of 1 returns '' outright. */
      if (v && v.per && v.count > 1) return v.per + '/ea';
      if (v && v.count === 1) return '';
      return variantId && unitPriceMap[variantId] ? unitPriceMap[variantId] : '';
    }

    function buildRow(optionName, optionValue, onSelect) {
      const live = findLiveOption(sectionRoot, optionName, optionValue);
      if (!live) return null;
      const opt = live.opt;
      const variantId = opt.getAttribute('data-variant-id');
      const sku = variantId && skuMap[variantId] ? skuMap[variantId] : '';
      // Real data found: Shopify/the stock custom-select already marks
      // genuinely sold-out variants with .is-unavailable (confirmed via
      // direct inspection). No separate JSON lookup needed, unlike
      // SKU/price.
      const isUnavailable = opt.classList.contains('is-unavailable');
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'llm-option-popup__row';
      if (opt.getAttribute('aria-selected') === 'true') {
        row.classList.add('is-selected');
      }
      if (isUnavailable) {
        row.classList.add('is-unavailable');
      }

      const rowName = document.createElement('span');
      rowName.className = 'llm-option-popup__row-name';
      rowName.textContent = optionValue;

      const rowSku = document.createElement('span');
      rowSku.className = 'llm-option-popup__row-sku';
      rowSku.textContent = sku ? ('SKU: ' + sku) : '';

      // Owner asked for SKU to sit on its own line under the option name
      // instead of sharing a row with it — grouping name+SKU into one
      // stacked column (with price staying on the right, its own
      // column) lets longer option names stay on a single line instead
      // of wrapping awkwardly next to the SKU/price text.
      const rowMain = document.createElement('span');
      rowMain.className = 'llm-option-popup__row-main';
      rowMain.appendChild(rowName);
      rowMain.appendChild(rowSku);

      // Owner asked to keep showing price for sold-out variants (not
      // replace it), with a separate "Out of stock" line underneath —
      // and to still let customers SELECT a sold-out variant (so the
      // Klaviyo "Notify me when available" trigger, which activates
      // based on which variant is currently selected, can show up). So
      // unlike the row's visual state, the click handler is never gated
      // off here.
      const rowPriceGroup = document.createElement('span');
      rowPriceGroup.className = 'llm-option-popup__row-price-group';

      const rowPrice = document.createElement('span');
      rowPrice.className = 'llm-option-popup__row-price';
      setMoney(rowPrice, variantId && priceMap[variantId] ? priceMap[variantId] : '');
      rowPriceGroup.appendChild(rowPrice);

      // Per-unit "$X.XX/ea" under the price (count packs only) — lets customers compare
      // per-unit across pack sizes right in the list.
      const rowUnitPrice = unitTextFor(variantId);
      if (rowUnitPrice) {
        const rowUnit = document.createElement('span');
        rowUnit.className = 'llm-option-popup__row-unit-price';
        setMoney(rowUnit, rowUnitPrice);
        rowPriceGroup.appendChild(rowUnit);
      }

      if (isUnavailable) {
        const rowOos = document.createElement('span');
        rowOos.className = 'llm-option-popup__row-oos';
        rowOos.textContent = 'Out of stock';
        rowPriceGroup.appendChild(rowOos);
      }

      row.appendChild(rowMain);
      row.appendChild(rowPriceGroup);

      row.addEventListener('click', function () {
        const freshLive = findLiveOption(sectionRoot, optionName, optionValue);
        if (freshLive) {
          if (freshLive.customSelectEl && typeof freshLive.customSelectEl.selectOption === 'function') {
            freshLive.customSelectEl.selectOption(freshLive.opt);
          } else {
            freshLive.opt.click();
          }
        }
        onSelect();
      });

      return row;
    }

    /* Owner asked to rethink the combined multi-option popup — showing
       a price on every one of 6+ color chips (etc.) per option group
       read as cluttered/confusing, and long option lists meant some
       option groups (e.g. Thickness, at the bottom) weren't visible
       without scrolling, risking a customer picking a color and style
       but never noticing/scrolling to Thickness and ordering the wrong
       one. Chips replace the old detailed rows for multi-option
       products only (single-option products, e.g. BetterDry's pack
       sizes, still use buildRow's full list — that's the customer's
       actual purchase decision there, so price-per-row still matters).
       Compact pill buttons (name only, no price/SKU) let every option
       group's full set of values usually fit without scrolling; the one
       price that actually matters — the full variant's — moves to a
       single summary line shared across all option groups instead. */
    function buildChip(optionName, optionValue, onSelect) {
      const live = findLiveOption(sectionRoot, optionName, optionValue);
      if (!live) return null;
      const opt = live.opt;
      const isUnavailable = opt.classList.contains('is-unavailable');
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'llm-option-popup__chip';
      if (opt.getAttribute('aria-selected') === 'true') {
        chip.classList.add('is-selected');
      }
      if (isUnavailable) {
        chip.classList.add('is-unavailable');
      }
      chip.textContent = optionValue;

      chip.addEventListener('click', function () {
        const freshLive = findLiveOption(sectionRoot, optionName, optionValue);
        if (freshLive) {
          if (freshLive.customSelectEl && typeof freshLive.customSelectEl.selectOption === 'function') {
            freshLive.customSelectEl.selectOption(freshLive.opt);
          } else {
            freshLive.opt.click();
          }
        }
        onSelect();
      });

      return chip;
    }

    /* Square option TILE — the same visual used by the inline buy-box grid (.llm-variant-tile):
       the option label split on "|" into stacked lines, plus price / unit price / Out-of-stock.
       Used both by the inline grid AND (owner request) by the single-option "+more options" popup,
       so the popup squares match the grid. Selecting re-resolves the CURRENT live option and calls
       Shopify's selectOption(), then runs onSelect (e.g. close the popup). */
    function buildTile(optionName, optionValue, onSelect) {
      const live = findLiveOption(sectionRoot, optionName, optionValue);
      if (!live) return null;
      const opt = live.opt;
      const variantId = opt.getAttribute('data-variant-id');
      const isUnavailable = opt.classList.contains('is-unavailable');
      const tile = document.createElement('button');
      tile.type = 'button';
      tile.className = 'llm-variant-tile';
      if (opt.getAttribute('aria-selected') === 'true') tile.classList.add('is-selected');
      if (isUnavailable) tile.classList.add('is-unavailable');

      const nm = document.createElement('span');
      nm.className = 'llm-variant-tile__name';
      optionValue.split('|').forEach(function (part) {
        const line = document.createElement('span');
        line.className = 'llm-variant-tile__name-line';
        line.textContent = part.trim();
        nm.appendChild(line);
      });
      tile.appendChild(nm);

      const priceText = variantId && priceMap[variantId] ? priceMap[variantId] : '';
      if (priceText) {
        const pr = document.createElement('span');
        pr.className = 'llm-variant-tile__price';
        setMoney(pr, priceText);
        tile.appendChild(pr);
      }
      /* BEST VALUE pill, overlaid on the tile's top border (owner, 2026-10-14). Only rendered when
         the Liquid map says this variant genuinely wins AND the saving cleared the 5% floor — the
         flag is not "cheapest per unit", because samples are subsidised and would otherwise take
         the badge off the case. All of that is decided in snippets/llm-variant-value-data.liquid;
         this is only the drawing.
         aria-hidden: the saving is already stated in the per-unit line right below, so a screen
         reader would otherwise hear the same claim twice. */
      const valueInfo = variantId && valueMap[variantId];
      if (valueInfo && valueInfo.best) {
        tile.classList.add('has-best-pill');
        const pill = document.createElement('span');
        pill.className = 'llm-variant-tile__pill';
        /* "Best value" ALONE. "Best value · save 5%" measured wider than the ~95px tile and spilled
           across its neighbour. The saving does not need stating here anyway — the per-unit price is
           two lines below and is the actual argument; the pill is just the flag pointing at it. */
        pill.textContent = 'Best value';
        pill.setAttribute('aria-hidden', 'true');
        tile.appendChild(pill);
      }

      const unitText = unitTextFor(variantId);
      if (unitText) {
        const u = document.createElement('span');
        u.className = 'llm-variant-tile__unit';
        /* Parenthesised, matching Amazon's "($4.25 / count)" (owner, 2026-09-21: "put the unit cost
           in parenthesis"). The source string is already money-formatted by variant-picker.liquid
           as "$4.25/ea", so this only adds the brackets and breathes out the slash.
           ⚠️ Done in JS, not with CSS ::before/::after. Generated content is not selectable, does
           not survive copy/paste, and is skipped or read inconsistently by screen readers — the
           brackets are part of the meaning here ("this is a per-unit price, not the price"), so
           they belong in the text. */
        setMoney(u, '(' + String(unitText).replace(/\s*\/\s*/, ' / ') + ')');
        tile.appendChild(u);
      }
      // SKU below the pricing (owner request 2026-07-29). skuMap is the same
      // scoped #llm-variant-sku-data map buildRow uses.
      const skuText = variantId && skuMap[variantId] ? skuMap[variantId] : '';
      if (skuText) {
        const sk = document.createElement('span');
        sk.className = 'llm-variant-tile__sku';
        sk.textContent = skuText;
        tile.appendChild(sk);
      }
      if (isUnavailable) {
        const oos = document.createElement('span');
        oos.className = 'llm-variant-tile__oos';
        oos.textContent = 'Out of stock';
        tile.appendChild(oos);
      } else if (llmIsLowStock(variantId)) {
        const low = document.createElement('span');
        low.className = 'llm-variant-tile__lowstock';
        var _lowQty = llmInvData[String(variantId)].q;
        low.textContent = 'Low stock (' + _lowQty + ')';
        tile.appendChild(low);
      }

      tile.addEventListener('click', function () {
        const fresh = findLiveOption(sectionRoot, optionName, optionValue);
        if (fresh) {
          if (fresh.customSelectEl && typeof fresh.customSelectEl.selectOption === 'function') {
            fresh.customSelectEl.selectOption(fresh.opt);
          } else {
            fresh.opt.click();
          }
        }
        if (typeof onSelect === 'function') onSelect();
      });
      return tile;
    }

    function currentSelectedText(optionName) {
      const selector = findLiveSelector(sectionRoot, optionName);
      if (!selector) return '';
      const opts = selector.querySelectorAll('.custom-select__option');
      const selected = selector.querySelector('.custom-select__option[aria-selected="true"]') || opts[0];
      return selected ? optionValueOf(selected) : '';
    }

    function currentSelectedSku(optionName) {
      const selector = findLiveSelector(sectionRoot, optionName);
      if (!selector) return '';
      const opts = selector.querySelectorAll('.custom-select__option');
      const selected = selector.querySelector('.custom-select__option[aria-selected="true"]') || opts[0];
      const variantId = selected ? selected.getAttribute('data-variant-id') : null;
      return variantId && skuMap[variantId] ? skuMap[variantId] : '';
    }

    const isMulti = optionNames.length > 1;

    // Amazon/Chewy-style inline tiles are used for SINGLE-option products on a real product
    // section only — NOT multi-option products (they keep the combined chip popup) and NOT the
    // quick-add drawer / any dialog (kept on the proven trigger+popup, which is compact for a
    // slideout). When false, everything below behaves exactly as before. Even if this gate were
    // wrong it can't break switching — tiles reuse the same live-option resolution as the popup.
    const DRAWER_SEL = 'quick-add-drawer, .quick-add-drawer, .drawer, [data-quick-add], dialog';
    const inDrawer = !!(firstOptionSelector.closest(DRAWER_SEL) || sectionRoot.closest(DRAWER_SEL));
    /* QUICK-BUY DRAWER (owner 2026-10-27: "update the quick buy slide out to the same variant option
       design as the PDP"). The drawer kept the trigger + popup because the old CARD grid was too big
       for a slide-out; the Size/Color/Package BUTTON picker is compact, so the drawer now uses it —
       but ONLY that: a product whose values do not split cleanly keeps the trigger + popup there
       rather than getting the card scroller. Same test as splitWanted() further down. */
    const drawerSplit = inDrawer && !isMulti && (function () {
      if (new URLSearchParams(location.search).get('split') === '0') return false;
      const vals = Array.from(firstOptionSelector.querySelectorAll('.custom-select__option')).map(optionValueOf);
      if (vals.length < 2) return false;
      const cnt = function (v) { return String(v).split('|').length; };
      const n0 = cnt(vals[0]);
      return vals.every(function (v) { return cnt(v) === n0; });
    })();
    /* SEPARATE Shopify options (owner 2026-10-27: Gary terry-lined pants "still have a drop down",
       and the mattress covers' Color/Style/Height). Same button rows as the "A | B" products, drawn
       from every variant's options (snippets/variant-picker.liquid). 27 active products then. */
    const multiData = isMulti ? readScopedDataMap(sectionRoot, 'llm-variant-options-data') : null;
    const multiSplit = isMulti && Array.isArray(multiData) && multiData.length > 1
      && new URLSearchParams(location.search).get('split') !== '0';
    const useInlineTiles = multiSplit || !isMulti && (drawerSplit || (
      typeof sectionRoot.id === 'string'
      && sectionRoot.id.indexOf('shopify-section-') === 0
      && !inDrawer));

    // Build the closed-state trigger.
    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'llm-option-trigger';

    // Owner asked for a visible arrow signifying this is a
    // selector/dropdown, not just plain text — name + chevron share a
    // row so the arrow reads as "attached" to the selected value,
    // matching how a native select's own arrow is positioned.
    const triggerNameRow = document.createElement('span');
    triggerNameRow.className = 'llm-option-trigger__name-row';

    const triggerName = document.createElement('span');
    triggerName.className = 'llm-option-trigger__name';

    const triggerChevron = document.createElement('span');
    triggerChevron.className = 'llm-option-trigger__chevron';
    triggerChevron.innerHTML = '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    triggerNameRow.appendChild(triggerName);
    triggerNameRow.appendChild(triggerChevron);

    const triggerSku = document.createElement('span');
    triggerSku.className = 'llm-option-trigger__sku';

    const triggerSeeAll = document.createElement('span');
    triggerSeeAll.className = 'llm-option-trigger__see-all';
    if (isMulti) {
      triggerSeeAll.textContent = 'Choose options';
    } else {
      const firstOptions = firstOptionSelector.querySelectorAll('.custom-select__option');
      triggerSeeAll.textContent = 'See all ' + firstOptions.length + ' options';
    }

    trigger.appendChild(triggerNameRow);
    trigger.appendChild(triggerSku);
    trigger.appendChild(triggerSeeAll);

    // Owner: a visible "Choose your Option" label above the trigger. The stock label
    // lives inside the option-selector, which we visually-hide wholesale — so surface
    // our own here, above the trigger, and keep it with the trigger on every re-anchor.
    const triggerLabel = document.createElement('span');
    triggerLabel.className = 'llm-option-trigger-label';
    // Owner: show "<Option name>: <currently-selected value>" (e.g. "Option: MD (29\" to 43\") | Pack of
    // 15") instead of a static "Choose your Option", updating live as tiles/options are picked. Split
    // into name + value spans so the name reads as a quiet label and the value is emphasized.
    // Owner 2026-10-24: a "Size Chart" link on the right of this label when the product has a chart;
    // llm-product-accordions.js scrolls to it (opening the mobile dropdown first). The chart is rendered
    // by llm-product-description.liquid only when custom.size_chart is set, so its presence is the test.
    // Main product section only (same test as useInlineTiles): a quick-add drawer shows a DIFFERENT
    // product, and this page's chart would be the wrong one.
    const hasSizeChart = !!document.querySelector('.llm-product-size-chart')
      && typeof sectionRoot.id === 'string'
      && sectionRoot.id.indexOf('shopify-section-') === 0
      && !sectionRoot.closest('quick-add-drawer, .quick-add-drawer, .drawer, [data-quick-add], dialog');
    // The link shares a row with the last line of the label (the SKU line, or "Choose your options"),
    // pushed right, so it adds no height to the buy box.
    function sizeChartRow(left) {
      const row = document.createElement('span');
      row.className = 'llm-option-trigger-label__meta';
      row.appendChild(left || document.createElement('span'));
      // A <button>, not <a href="#…">: with a hash link something else on the page also scrolls to the
      // hash target, and on mobile that target is the hidden desktop chart - it won the race and left
      // the window at the top (measured 2026-10-24). A button gets only our handler.
      const a = document.createElement('button');
      a.type = 'button';
      a.className = 'llm-size-chart-link';
      a.textContent = 'Size Chart';
      row.appendChild(a);
      return row;
    }
    // Set by the tile renderer when a single-option product ("A | B | C" values) is drawn as split rows.
    let singleSplit = false;
    function updateTriggerLabel() {
      // Owner 2026-10-28: the split rows each show their own "Size: X" heading, so the combined
      // "Option: White | XS | Trial Pack of 4" line repeated them. Split pickers keep only SKU + Size Chart.
      if (multiSplit || singleSplit) {
        // The rows carry their own "Size: X" headings; this line keeps the SKU + Size Chart link.
        triggerLabel.textContent = '';
        const _sku = currentSelectedSku(optionNames[0]);
        let _ms = null;
        if (_sku) { _ms = document.createElement('span'); _ms.className = 'llm-option-trigger-label__sku'; _ms.textContent = 'SKU: ' + _sku; }
        if (hasSizeChart) triggerLabel.appendChild(sizeChartRow(_ms));
        else if (_ms) triggerLabel.appendChild(_ms);
        return;
      }
      if (isMulti) {
        triggerLabel.textContent = hasSizeChart ? '' : 'Choose your options';
        if (hasSizeChart) triggerLabel.appendChild(sizeChartRow(document.createTextNode('Choose your options')));
        return;
      }
      triggerLabel.textContent = '';
      const _n = document.createElement('span');
      _n.className = 'llm-option-trigger-label__name';
      _n.textContent = optionNames[0] + ': ';
      const _v = document.createElement('span');
      _v.className = 'llm-option-trigger-label__value';
      _v.textContent = currentSelectedText(optionNames[0]);
      triggerLabel.appendChild(_n);
      triggerLabel.appendChild(_v);
      // Owner 2026-07-29: show the selected variant's SKU next to the option value,
      // above the tiles. Updates live via updateTrigger() on every option change.
      const _skuVal = currentSelectedSku(optionNames[0]);
      let _s = null;
      if (_skuVal) {
        _s = document.createElement('span');
        _s.className = 'llm-option-trigger-label__sku';
        _s.textContent = 'SKU: ' + _skuVal;
      }
      if (hasSizeChart) triggerLabel.appendChild(sizeChartRow(_s));
      else if (_s) triggerLabel.appendChild(_s);
    }
    updateTriggerLabel();

    function updateTrigger() {
      updateTriggerLabel();
      if (isMulti) {
        // Combine every option group's currently selected value into
        // one line (e.g. "Milky White (7 gauge) / Elastic Corners / 8
        // inch (standard)"). Owner asked for the SKU to also show here
        // — there's no single SKU per option group, only per full
        // variant, so triggerSku's text is set separately, from the
        // same on:variant:change-driven value already used for the
        // popup's own summary line (see below), not computed here.
        const parts = optionNames.map(function (name) {
          return currentSelectedText(name);
        });
        triggerName.textContent = parts.join(' / ');
      } else {
        triggerName.textContent = currentSelectedText(optionNames[0]);
        const sku = currentSelectedSku(optionNames[0]);
        triggerSku.textContent = sku ? ('SKU: ' + sku) : '';
      }
    }
    updateTrigger();

    // Build the popup overlay + panel.
    const overlay = document.createElement('div');
    overlay.className = 'llm-option-popup-overlay';

    const panel = document.createElement('div');
    panel.className = 'llm-option-popup';

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'llm-option-popup__close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML = '&times;';

    const header = document.createElement('div');
    header.className = 'llm-option-popup__header';

    const heading = document.createElement('div');
    heading.className = 'llm-option-popup__heading';
    heading.textContent = 'Choose an option';

    header.appendChild(heading);
    header.appendChild(closeBtn);

    function closePopup() {
      overlay.classList.remove('is-open');
      document.body.style.overflow = '';
    }

    // Owner asked for the one price/SKU/availability that actually
    // matters — the full current variant, not a price per chip — to
    // live in a single summary line shared by every option group,
    // updating live as any group's selection changes. Sourced from
    // variant-picker.js's own `on:variant:change` event, which carries
    // the true current variant object regardless of which option
    // group triggered the change.
    let summaryPrice = null;
    let summaryUnitPrice = null;
    let summarySku = null;
    let summaryAvailability = null;
    if (isMulti) {
      const summary = document.createElement('div');
      summary.className = 'llm-option-popup__summary';

      summaryPrice = document.createElement('span');
      summaryPrice.className = 'llm-option-popup__summary-price';

      summaryUnitPrice = document.createElement('span');
      summaryUnitPrice.className = 'llm-option-popup__summary-unit-price';

      summarySku = document.createElement('span');
      summarySku.className = 'llm-option-popup__summary-sku';

      summaryAvailability = document.createElement('span');
      summaryAvailability.className = 'llm-option-popup__summary-availability';

      summary.appendChild(summaryPrice);
      summary.appendChild(summaryUnitPrice);
      summary.appendChild(summarySku);
      summary.appendChild(summaryAvailability);
      panel.appendChild(summary);

      document.addEventListener('on:variant:change', function (evt) {
        const variant = evt.detail && evt.detail.variant;
        if (!variant || !priceMap.hasOwnProperty(variant.id)) return;
        setMoney(summaryPrice, priceMap[variant.id] || '');
        if (summaryUnitPrice) setMoney(summaryUnitPrice, unitPriceMap[variant.id] || '');
        summarySku.textContent = variant.sku ? ('SKU: ' + variant.sku) : (skuMap[variant.id] ? 'SKU: ' + skuMap[variant.id] : '');
        // Owner asked for the trigger's own closed-state SKU line to
        // show for multi-option products too — reusing this same
        // event/value rather than computing it separately, since
        // there's no single option-group's own data-variant-id that
        // reliably reflects the full combined variant (see the
        // summary's own comment above for why).
        triggerSku.textContent = summarySku.textContent;
        const isUnavailable = !variant.available;
        summaryAvailability.textContent = isUnavailable ? 'Out of stock' : '';
        summaryAvailability.classList.toggle('is-unavailable', isUnavailable);
        updateTrigger();
        refreshAllSelected();
      });
    }

    // Every chip/row's highlight is re-synced here, on the SAME
    // on:variant:change handler above (so refreshing only ever
    // happens after Shopify's own variant state has genuinely
    // settled — its announceChange() is itself wrapped in a
    // setTimeout(..., 10), confirming this is asynchronous, not
    // instantaneous), by re-resolving each entry's live option node
    // fresh rather than reading a cached one.
    const allEntries = []; // {optionName, optionValue, el}
    function refreshAllSelected() {
      allEntries.forEach(function (entry) {
        const live = findLiveOption(sectionRoot, entry.optionName, entry.optionValue);
        entry.el.classList.toggle('is-selected', !!(live && live.opt.getAttribute('aria-selected') === 'true'));
      });
      // Only now does a tile actually carry .is-selected, so this is the earliest point the row can
      // be scrolled to it. No-op unless the selected tile is off-screen.
      ensureSelectedVisible();
    }

    function updateSummary() {
      if (!isMulti) return;
      const firstName = optionNames[0];
      const selector = findLiveSelector(sectionRoot, firstName);
      if (!selector) return;
      const opts = selector.querySelectorAll('.custom-select__option');
      const selected = selector.querySelector('.custom-select__option[aria-selected="true"]') || opts[0];
      if (!selected) return;
      const variantId = selected.getAttribute('data-variant-id');
      const isUnavailable = selected.classList.contains('is-unavailable');
      setMoney(summaryPrice, variantId && priceMap[variantId] ? priceMap[variantId] : '');
      if (summaryUnitPrice) setMoney(summaryUnitPrice, unitTextFor(variantId));
      const sku = variantId && skuMap[variantId] ? skuMap[variantId] : '';
      summarySku.textContent = sku ? ('SKU: ' + sku) : '';
      triggerSku.textContent = summarySku.textContent;
      summaryAvailability.textContent = isUnavailable ? 'Out of stock' : '';
      summaryAvailability.classList.toggle('is-unavailable', isUnavailable);
    }
    updateSummary();

    optionNames.forEach(function (optionName) {
      const selector = findLiveSelector(sectionRoot, optionName);
      if (!selector) return;
      const values = Array.from(selector.querySelectorAll('.custom-select__option')).map(optionValueOf);
      if (!values.length) return;

      if (isMulti) {
        const sectionHeading = document.createElement('div');
        sectionHeading.className = 'llm-option-popup__section-heading';
        sectionHeading.textContent = optionName;
        panel.appendChild(sectionHeading);
      }

      const list = document.createElement('div');
      // Multi-option keeps compact name-only chips; single-option now uses the same square tiles
      // as the inline buy-box grid (owner request), 2–3 per row (see llm-variant-tiles.css).
      list.className = isMulti ? 'llm-option-popup__chip-grid' : 'llm-option-popup__tile-grid';
      values.forEach(function (optionValue) {
        const onSelect = function () {
          if (!isMulti) closePopup();
          updateTrigger();
          updateSummary();
        };
        const el = isMulti
          ? buildChip(optionName, optionValue, onSelect)
          : buildTile(optionName, optionValue, onSelect);
        if (!el) return;
        allEntries.push({ optionName: optionName, optionValue: optionValue, el: el });
        list.appendChild(el);
      });
      panel.appendChild(list);
    });

    // Owner asked for an explicit way to close the popup once done
    // choosing — for multi-option products the popup deliberately
    // stays open after each selection (so a customer can pick color,
    // then style, then height in sequence), but that also means
    // there's no equivalent to the single-option popup's auto-close
    // on pick, and not every customer will think to tap the X or click
    // outside. Single-option products don't need this: picking a
    // value there already closes the popup immediately (see
    // onSelect above).
    if (isMulti) {
      const doneBtn = document.createElement('button');
      doneBtn.type = 'button';
      doneBtn.className = 'llm-option-popup__done';
      doneBtn.textContent = 'Done';
      doneBtn.addEventListener('click', closePopup);
      panel.appendChild(doneBtn);
    }

    panel.insertBefore(header, panel.firstChild);
    overlay.appendChild(panel);
    document.body.appendChild(overlay);

    function openPopup() {
      // Re-sync every tile/chip's is-selected highlight to the CURRENT live selection before
      // showing the popup. Fixes: on single-option products the popup's tiles were built once at
      // init and only refreshed on the isMulti-gated variant:change handler, so selecting via the
      // inline tiles left the popup still highlighting the build-time default (owner 2026-07-29).
      refreshAllSelected();
      overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }

    trigger.addEventListener('click', openPopup);
    closeBtn.addEventListener('click', closePopup);
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) closePopup();
    });

    /* ---- Amazon/Chewy-style inline variant tiles (single-option main PDP) ----
       Renders the option values as selectable tiles right in the buy box instead of a closed
       "See all N options" trigger. Each tile carries the option name + price + unit price + an
       Out-of-stock line, and selecting it re-resolves the CURRENT live option and calls Shopify's
       own selectOption() — identical plumbing to the popup rows, so price/URL/Add-to-Cart all
       update exactly as before. When there are more than TILE_CAP options, the last cell becomes a
       "+N more options" tile that opens the popup built above. tilesWrap stays null (and the
       classic trigger is used) whenever useInlineTiles is false. */
    let tilesWrap = null;
    // (Re)builds the inline tile grid from scratch to reflect the CURRENT selection. Called on
    // first build and again on every on:variant:change — Shopify re-renders the option markup and
    // the newly-selected value may be one that was in the overflow, so we recompute which values
    // are visible (the selected one is always shown) rather than just toggling a class. No-op when
    // inline tiles aren't in use (multi-option / quick-add), so the variant:change handler can call
    // it unconditionally.
    let renderTiles = function () {};
    // Faux always-visible scrollbar under the mobile card row (Amazon-style). Mobile touch browsers
    // ignore ::-webkit-scrollbar and show a fade-out overlay bar instead, so we draw + drive our own.
    // Declared at function scope so the insert/reattach code below (outside the useInlineTiles block)
    // can place it next to the tile row; updateScrollbar sizes/positions the thumb from scroll metrics.
    let scrollbarEl = null;
    let updateScrollbar = function () {};
    // Assigned inside the inline-tiles branch below. Declared out here — like updateScrollbar —
    // because refreshAllSelected() has to call it: that is what applies .is-selected, and it runs
    // AFTER renderTiles, so at render time no tile is marked selected yet.
    let ensureSelectedVisible = function () {};
    // Last user scroll position of the tile row, captured live on every scroll. Restored after each
    // rebuild so selecting an option doesn't snap the row back to the first cards. Captured HERE (on
    // scroll) rather than inside renderTiles because the variant-change re-render zeroes scrollLeft
    // before renderTiles runs — by then it's already lost.
    let lastScrollLeft = 0;
    if (useInlineTiles) {
      const name = optionNames[0];

      tilesWrap = document.createElement('div');
      tilesWrap.className = 'llm-variant-tiles';

      scrollbarEl = document.createElement('div');
      scrollbarEl.className = 'llm-variant-scrollbar';
      const scrollbarThumb = document.createElement('div');
      scrollbarThumb.className = 'llm-variant-scrollbar__thumb';
      // The thumb is decoration; the arrows below are real controls, so aria-hidden moved off the
      // track onto the thumb — inside an aria-hidden container the buttons would be invisible to
      // screen readers and unreachable by keyboard.
      scrollbarThumb.setAttribute('aria-hidden', 'true');
      scrollbarEl.appendChild(scrollbarThumb);

      /* Prev/next arrows at each end of the bar (owner, 2026-09-04), shown only between 500 and
         1000px — see llm-variant-tiles.css. Absolutely positioned OUTSIDE the track's horizontal
         bounds rather than added as siblings, so the insert/reattach code further down keeps working
         on a single element. */
      /* SMOOTH SCROLLING ON DESKTOP (owner 2026-10-26: "make the scrolling more smooth on desktop").
         Arrows and the mouse wheel used to set scrollLeft in one jump and let scroll-snap pull the row
         onto a tile edge. On desktop, snap is now off (llm-variant-tiles.css) and both glide here with
         a short ease-out instead. A wheel tick that lands mid-glide extends the same glide, so a fast
         spin of the wheel is one continuous movement rather than a series of jerks. Phones keep the
         direct assignment and their native snap. */
      const glideMode = function () { return window.matchMedia('(min-width: 1000px)').matches; };
      let glideRaf = 0, glideTarget = 0;
      const stopGlide = function () { if (glideRaf) { cancelAnimationFrame(glideRaf); glideRaf = 0; } };
      const glideTo = function (target) {
        const max = Math.max(0, tilesWrap.scrollWidth - tilesWrap.clientWidth);
        glideTarget = Math.min(Math.max(target, 0), max);
        if (!glideMode() || !window.requestAnimationFrame) {
          tilesWrap.scrollLeft = glideTarget; updateScrollbar(); return;
        }
        stopGlide();
        const from = tilesWrap.scrollLeft, dist = glideTarget - from, t0 = performance.now(), dur = 320;
        const step = function (now) {
          const p = Math.min((now - t0) / dur, 1);
          const eased = 1 - Math.pow(1 - p, 3);          // ease-out cubic
          tilesWrap.scrollLeft = from + dist * eased;
          updateScrollbar();
          glideRaf = p < 1 ? requestAnimationFrame(step) : 0;
        };
        glideRaf = requestAnimationFrame(step);
      };

      const mkArrow = function (dir) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'llm-variant-scrollbar__arrow llm-variant-scrollbar__arrow--' + dir;
        b.setAttribute('aria-label', dir === 'prev' ? 'Scroll options left' : 'Scroll options right');
        b.innerHTML = '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="'
          + (dir === 'prev' ? 'M10 3L5 8l5 5' : 'M6 3l5 5-5 5')
          + '" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        // The track listens for pointerdown to jump-scroll; without this a click on an arrow would
        // also be read as a click on the track underneath it and fight the scroll it just started.
        b.addEventListener('pointerdown', function (e) { e.stopPropagation(); });
        b.addEventListener('click', function (e) {
          e.stopPropagation();
          // One "page" less a tile, so the tile at the edge stays visible as an anchor.
          const step = Math.max(tilesWrap.clientWidth - 148, 148);
          const max = Math.max(0, tilesWrap.scrollWidth - tilesWrap.clientWidth);
          const target = tilesWrap.scrollLeft + (dir === 'prev' ? -step : step);
          /* Assigned directly, NOT scrollBy/scrollTo with behavior:'smooth'. This row sets
             scroll-snap-type:x, and a smooth programmatic scroll against it is silently dropped —
             verified live: behavior:'smooth' left scrollLeft at 0 across 8 samples while
             behavior:'auto' moved it. Snap still animates the landing, so it does not look abrupt. */
          // Desktop: glide (no snap there to drop it). Phones: the direct assignment described above.
          if (glideMode()) { glideTo(target); return; }
          tilesWrap.scrollLeft = Math.min(Math.max(target, 0), max);
          // Refresh thumb + arrow disabled states here rather than waiting for the scroll event.
          // Everything below drives scrollLeft programmatically, and a programmatic scroll's event
          // is not guaranteed to have fired by the time the next click lands — leaving "prev" stuck
          // disabled from init, so it silently did nothing.
          updateScrollbar();
        });
        return b;
      };
      const arrowPrev = mkArrow('prev');
      const arrowNext = mkArrow('next');
      scrollbarEl.appendChild(arrowPrev);
      scrollbarEl.appendChild(arrowNext);

      updateScrollbar = function () {
        const sw = tilesWrap.scrollWidth;
        const cw = tilesWrap.clientWidth;
        if (sw <= cw + 1) { scrollbarEl.style.display = 'none'; return; } // no overflow → no bar
        scrollbarEl.style.display = 'block';
        const thumbW = Math.max((cw / sw) * 100, 12); // % of track, floored so it stays grabbable-looking
        const maxScroll = sw - cw;
        const pos = maxScroll > 0 ? tilesWrap.scrollLeft / maxScroll : 0;
        scrollbarThumb.style.width = thumbW + '%';
        scrollbarThumb.style.left = (pos * (100 - thumbW)) + '%';
        // Grey out an arrow once there is nothing further that way. 1px of slack because
        // scrollLeft is fractional on zoomed/HiDPI displays and never lands exactly on maxScroll.
        arrowPrev.disabled = tilesWrap.scrollLeft <= 1;
        arrowNext.disabled = tilesWrap.scrollLeft >= maxScroll - 1;
      };
      /* Bring the SELECTED tile into view horizontally.
         The row starts at scrollLeft 0, so when the chosen variant is not among the first few tiles
         it is simply not on screen — on NorthShore MEGAMAX the first three tiles are the sold-out XS
         sizes, putting the selected "SM | Trial Pack of 4" at tile 4, measured 13px past the row's
         right edge. The customer sees an unhighlighted row and no indication anything is selected
         (owner 2026-09-06).

         ⚠️ scrollLeft ONLY — never scrollIntoView(). This runs during first paint and on every
         variant re-render; scrollIntoView would drag the whole PAGE vertically to the buy box each
         time, which is the same class of jump just fixed for the FBT picker.

         Minimal movement, and self-limiting: it does nothing when the tile is already visible. So a
         customer who has scrolled the row to browse is never yanked back — clicking a tile they can
         see cannot trigger it — while a selection changed from elsewhere (the popup, the FBT block,
         a ?variant= URL) is always brought into view. PEEK leaves a sliver of the previous tile
         showing so the row still reads as scrollable rather than as if it started there. */
      ensureSelectedVisible = function () {
        var sel = tilesWrap.querySelector('.llm-variant-tile.is-selected');
        if (!sel) return false;
        var max = tilesWrap.scrollWidth - tilesWrap.clientWidth;
        if (max <= 0) return false;                 // not laid out yet, or nothing to scroll
        var wrapR = tilesWrap.getBoundingClientRect();
        var selR = sel.getBoundingClientRect();
        if (selR.left >= wrapR.left - 1 && selR.right <= wrapR.right + 1) return true; // in view

        /* Align the tile's START to the row's start. That is NOT a stylistic choice — the row is
           `scroll-snap-type: x` with `scroll-snap-align: start` on each tile, so the only scroll
           offsets that survive are tile starts. An earlier version aimed for a 24px "peek" of the
           previous tile and the browser silently snapped it elsewhere: asking for 409 landed on 444,
           leaving the tile 5px past the right edge no matter how many times it retried. */
        var padLeft = parseFloat(getComputedStyle(tilesWrap).paddingLeft) || 0;
        var target = tilesWrap.scrollLeft + (selR.left - wrapR.left) - padLeft;
        tilesWrap.scrollLeft = Math.min(Math.max(target, 0), max);
        // Read BACK rather than storing the requested value — snapping adjusts it, and the restore
        // position has to match what the row is actually at.
        lastScrollLeft = tilesWrap.scrollLeft;
        /* Report whether the tile ACTUALLY ended up in view, not merely that the maths ran. The row
           reflows after this first fires — late webfonts and images change tile widths — and a
           one-shot alignment computed against the pre-reflow layout landed the tile 5px short of the
           right edge. Returning the real outcome lets the retry ladder correct itself. */
        var a = sel.getBoundingClientRect();
        var w = tilesWrap.getBoundingClientRect();
        return a.left >= w.left - 1 && a.right <= w.right + 1;
      };

      /* renderTiles() finishes before the row is laid out — tilesWrap is built and filled but is not
         yet in the document (it is inserted further down as `primaryAnchor`), so clientWidth is 0,
         scrollWidth - clientWidth is 0, and the alignment above has nothing to measure. A single rAF
         was not enough either. Retry on a short ladder and stop the moment it measures; each attempt
         is a no-op once the tile is in view, so overshooting costs nothing. */
      function alignSelectedWhenReady(tries) {
        if (ensureSelectedVisible()) return;
        if (tries <= 0) return;
        setTimeout(function () { alignSelectedWhenReady(tries - 1); }, 80);
      }

      tilesWrap.addEventListener('scroll', function () {
        lastScrollLeft = tilesWrap.scrollLeft;
        updateScrollbar();
      }, { passive: true });
      window.addEventListener('resize', updateScrollbar);

      /* MOUSE SUPPORT for the scroll row.
         This row used to exist only under 700px, i.e. touch devices, where swiping scrolls it and
         the bar below only had to INDICATE position. It now runs up to 1000px (owner, 2026-09-04),
         which covers plenty of mouse-driven windows — and there a mouse has no way to move it: the
         native scrollbar is hidden in CSS, the bar was decorative (aria-hidden, no handlers), and a
         vertical wheel does nothing to a horizontal overflow. Reported as "the scroll bar does
         nothing", correctly.

         Three additions, all pointer-based so touch keeps working unchanged:
           1. drag the thumb,
           2. click the track to jump,
           3. vertical wheel over the row scrolls it horizontally. */
      const scrollMax = () => Math.max(0, tilesWrap.scrollWidth - tilesWrap.clientWidth);

      // Track/thumb geometry: the thumb spans (100 - thumbW)% of the track, so a click at ratio r
      // of the TRACK maps to r of the scrollable distance, adjusted for the thumb's own width.
      /* grabOffset: where on the THUMB the pointer went down. Dragging keeps that point under the
         cursor, so grabbing the thumb near its edge no longer snaps its centre to the pointer — that
         jump on first touch was a big part of the bar feeling rougher than dragging the tiles
         (owner 2026-10-26). A press on the bare TRACK still centres the thumb there, and on desktop
         glides to it rather than jumping. */
      let grabOffset = null;
      const pointerTarget = function (clientX) {
        const track = scrollbarEl.getBoundingClientRect();
        const thumb = scrollbarThumb.getBoundingClientRect();
        const usable = track.width - thumb.width;
        if (usable <= 0) return null;
        const x = clientX - track.left - (grabOffset === null ? thumb.width / 2 : grabOffset);
        return Math.min(Math.max(x / usable, 0), 1) * scrollMax();
      };
      const scrollToPointer = function (clientX) {
        const t = pointerTarget(clientX);
        if (t === null) return;
        tilesWrap.scrollLeft = t;
        updateScrollbar();          // see the note in the arrow click handler
      };

      let dragging = false;
      scrollbarEl.addEventListener('pointerdown', function (e) {
        stopGlide();
        dragging = true;
        scrollbarEl.setPointerCapture(e.pointerId);   // keeps tracking if the cursor leaves the bar
        scrollbarEl.classList.add('is-dragging');
        const th = scrollbarThumb.getBoundingClientRect();
        if (e.clientX >= th.left && e.clientX <= th.right) {
          grabOffset = e.clientX - th.left;             // on the thumb: no jump, just start dragging
        } else {
          grabOffset = null;                            // on the track: move there
          const t = pointerTarget(e.clientX);
          if (t !== null) { if (glideMode()) glideTo(t); else scrollToPointer(e.clientX); }
          grabOffset = scrollbarThumb.offsetWidth / 2;  // then drag from the thumb's centre
        }
        e.preventDefault();                            // no text selection while dragging
      });
      scrollbarEl.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        stopGlide();                                    // a track-click glide yields to the drag
        scrollToPointer(e.clientX);
      });
      const endDrag = function (e) {
        if (!dragging) return;
        dragging = false;
        grabOffset = null;
        scrollbarEl.classList.remove('is-dragging');
        try { scrollbarEl.releasePointerCapture(e.pointerId); } catch (err) {}
      };
      scrollbarEl.addEventListener('pointerup', endDrag);
      scrollbarEl.addEventListener('pointercancel', endDrag);

      // A trackpad already sends deltaX; only translate deltaY, and only while the row can actually
      // scroll that way — otherwise the page would stop scrolling vertically over the tiles.
      tilesWrap.addEventListener('wheel', function (e) {
        if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
        const max = scrollMax();
        if (!max) return;
        // Mid-glide, build on where the glide is heading, not where it currently is.
        const base = (glideMode() && glideRaf) ? glideTarget : tilesWrap.scrollLeft;
        const next = base + e.deltaY;
        if ((base <= 0 && e.deltaY < 0) || (base >= max - 1 && e.deltaY > 0)) return;  // let the page take it at the ends
        if (glideMode()) glideTo(next);
        else { tilesWrap.scrollLeft = next; updateScrollbar(); }   // see the note in the arrow click handler
        e.preventDefault();
      }, { passive: false });

      /* CLICK-AND-DRAG ANYWHERE ON THE ROW (owner 2026-10-26: "click anywhere in the options box and
         hold the mouse button and drag the options"). Mouse only — touch already swipes natively and
         pen/touch must keep their tap behaviour. A press that moves less than 6px is still a normal
         click on the tile; once it passes 6px it becomes a drag, and the click that the browser fires
         on release is swallowed so the drag does not also select whichever tile it ended on.
         Scroll-snap is paused while dragging, or the row would fight the pointer tile by tile. */
      (function () {
        let down = false, moved = false, startX = 0, startLeft = 0, pid = null;
        tilesWrap.addEventListener('pointerdown', function (e) {
          if (e.pointerType !== 'mouse' || e.button !== 0) return;
          if (tilesWrap.scrollWidth <= tilesWrap.clientWidth + 1) return;   // nothing to drag
          stopGlide();
          down = true; moved = false; startX = e.clientX; startLeft = tilesWrap.scrollLeft; pid = e.pointerId;
        });
        tilesWrap.addEventListener('pointermove', function (e) {
          if (!down || e.pointerId !== pid) return;
          const dx = e.clientX - startX;
          if (!moved) {
            if (Math.abs(dx) < 6) return;
            moved = true;
            tilesWrap.classList.add('is-drag-scrolling');
            try { tilesWrap.setPointerCapture(pid); } catch (err) {}
          }
          tilesWrap.scrollLeft = startLeft - dx;
          updateScrollbar();
          e.preventDefault();
        });
        const end = function (e) {
          if (!down || (e && e.pointerId !== pid)) return;
          down = false;
          if (moved) {
            tilesWrap.classList.remove('is-drag-scrolling');
            try { tilesWrap.releasePointerCapture(pid); } catch (err) {}
            // Swallow exactly the click this release produces, then stop listening.
            const swallow = function (ev) { ev.preventDefault(); ev.stopPropagation(); };
            tilesWrap.addEventListener('click', swallow, true);
            setTimeout(function () { tilesWrap.removeEventListener('click', swallow, true); }, 0);
          }
        };
        tilesWrap.addEventListener('pointerup', end);
        tilesWrap.addEventListener('pointercancel', end);
        // Tiles contain images and links; a native drag-and-drop ghost would hijack the gesture.
        tilesWrap.addEventListener('dragstart', function (e) { e.preventDefault(); });
      })();

      /* ===== DESKTOP PAGINATION (owner, 2026-09-21) ==========================================
         Modelled on Amazon's variant twister, measured on the PDP the owner linked
         (amazon.com/dp/B0D38J6DBM): SIX tiles per page in 2 rows of 3, ~93x96px each, with a
         numbered pager `< 1 [2] 3 4 >` underneath. 22 variants over 4 pages. Amazon has NO
         overflow-to-popup at all — the block never grows past two rows, you page through it.

         Owner: "our 3 rows is too much... ours just look gigantic and not as nice. I think people
         are more used to what Amazon shows. Maybe only show 4 or 5 pages max and then have the
         'Show all' card on the last page."

         ⚠️ PAGE SIZE IS MEASURED, NOT ASSUMED. It is (rendered column count x 2 rows). The CSS uses
         `auto-fill`, so the column count is whatever the PDP's buy-box column can hold — 4 here,
         3 in a narrower one. Hard-coding 6 to match Amazon's number would give two rows on THEIR
         layout and a ragged partial row on ours. Read the tracks, multiply by rows.
         Mobile (<1000px) is untouched: a horizontal scroller, where paging would be worse than
         swiping, keeps its 12-tile cap and "+N more" cell. */
      var PAGE_COLS = 3;          // usable columns; the 4th is the faded peek at the next page
      var PAGE_ROWS = 2;
      var MAX_PAGES = 5;          // beyond this the last cell becomes "Show all"
      var tilePage = null;        // null = open on the SELECTED variant's page, not page 1
      var pagerEl = null;
      var _refocusPager = false;  // set by a pager click; consumed by renderPager after the rebuild

      /* PAGINATION OFF (owner 2026-10-26: "I'm not liking the pagination... first just give me a scroll
         bar at the bottom instead"). Desktop now uses the same scroller branch as phones, laid out as
         two rows in llm-variant-tiles.css, with the faux scrollbar + arrows and mouse drag-to-scroll.
         The pager code below is left in place, dormant. TO RESTORE PAGING: put back
         `return window.matchMedia('(min-width: 1000px)').matches;` and remove the 2026-10-26 block at
         the end of llm-variant-tiles.css. */
      function tileGridMode() { return false; }

      /* auto-fill generates its track list from the container width even when the grid is empty,
         so this is safe to call before any tile is appended. */
      function gridColumns() {
        var t = getComputedStyle(tilesWrap).gridTemplateColumns;
        if (!t || t === 'none') return 4;
        var n = t.trim().split(/\s+/).filter(Boolean).length;
        return Math.max(1, Math.min(n, 8));
      }

      function moreTile(label, sub, hint) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'llm-variant-tile llm-variant-tile--more';
        b.innerHTML =
          '<span class="llm-variant-tile__more-count">' + label + '</span>' +
          '<span class="llm-variant-tile__more-label">' + sub + '</span>' +
          (hint ? '<span class="llm-variant-tile__more-hint">' + hint + '</span>' : '');
        b.addEventListener('click', openPopup);
        return b;
      }

      /* An empty grid cell. Invisible and untabbable, but it OCCUPIES a column — without it the
         next tile flows into the gap and the page silently reflows. See the interleave below. */
      function spacerCell() {
        var s = document.createElement('span');
        s.className = 'llm-variant-tile__spacer';
        s.setAttribute('aria-hidden', 'true');
        return s;
      }

      function renderPager(pages, current) {
        if (pagerEl) { pagerEl.remove(); pagerEl = null; }
        if (pages <= 1) return;
        var nav = document.createElement('nav');
        nav.className = 'llm-variant-pager';
        nav.setAttribute('aria-label', 'Variant pages');
        function btn(txt, cls, target, disabled, aria) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'llm-variant-pager__' + cls;
          b.textContent = txt;
          if (aria) b.setAttribute('aria-label', aria);
          if (disabled) { b.disabled = true; }
          else b.addEventListener('click', function () {
            tilePage = target;
            /* ⚠️ The pager is rebuilt by renderTiles(), so the button that was just clicked is
               DESTROYED mid-click. Focus falls back to <body>, and the browser scrolls to wherever
               that lands — the second half of the owner's "some page movement". Re-focus the
               equivalent button afterwards, with preventScroll so restoring focus does not itself
               scroll. The first half was the grid collapsing on a short page; see the 200px
               min-height in llm-variant-tiles.css. */
            _refocusPager = true;
            renderTiles();
          });
          return b;
        }
        nav.appendChild(btn('‹', 'arrow', current - 1, current === 0, 'Previous page'));
        for (var i = 0; i < pages; i++) {
          var p = btn(String(i + 1), 'page', i, false);
          if (i === current) { p.classList.add('is-current'); p.setAttribute('aria-current', 'true'); }
          nav.appendChild(p);
        }
        nav.appendChild(btn('›', 'arrow', current + 1, current === pages - 1, 'Next page'));
        /* ⚠️ APPENDED INSIDE THE GRID (spanning every column via CSS `grid-column: 1 / -1`), NOT as
           a sibling. The first version used insertAdjacentElement('afterend'), which silently did
           NOTHING: renderTiles() runs during build, BEFORE the theme inserts tilesWrap into the
           page, so there is no parent to insert next to and the pager never appeared. Living inside
           tilesWrap also means it survives every later move/reattach the theme does to the grid —
           a sibling would be orphaned by those. */
        tilesWrap.appendChild(nav);
        pagerEl = nav;
        if (_refocusPager) {
          _refocusPager = false;
          var cur = nav.querySelector('.llm-variant-pager__page.is-current');
          // preventScroll is the point of this — focus() without it scrolls the element into view,
          // which is the exact jump we are trying to remove.
          if (cur && cur.focus) { try { cur.focus({ preventScroll: true }); } catch (e) { /* older browsers */ } }
        }
      }


      /* ===== SPLIT PICKER: Color -> Size -> Package (owner 2026-10-26, testing on MEGAMAX) ===========
         Owner: "it can be tough to read those cards and find the size and package size, especially
         for the Megamax because there are option attributes (Color, Size, Package Size)."
         These products have ONE Shopify option whose values pack several attributes together:
             "White | XS (18\" to 30\") | Trial Pack of 4"     (81 of them on MEGAMAX)
         The cards printed those as stacked lines, so a shopper had to read every card. This mode
         splits each value on "|" and shows one picker per attribute: buttons for every attribute but
         the last, then cards for the last (the package), each with its price.
         · Pickers are HIERARCHICAL in the option's own order: sizes are those that exist in the chosen
           colour, packages those that exist in that colour + size. A combination that does not exist
           is greyed out and cannot be chosen, never silently swapped.
         · Choosing a button selects a real variant straight away (Shopify needs one selected at all
           times): the one that keeps the shopper's later choices if it exists, else the first in-stock
           one, else the first. Everything still goes through the hidden <custom-select>, exactly as
           the cards do, so price, stock, images and Add to cart follow as before.
         · Parts are TRIMMED before comparing — MEGAMAX has "Black " with a trailing space on one
           variant, which would otherwise be its own Black button.
         SITE-WIDE since 2026-10-26 (owner, after testing on MEGAMAX: "This looks great. Push this
           site wide"). Measured scope then: 637 active products, 574 of them Size > Package.
           ?split=0 on a product URL shows the old cards, for comparison or if one ever looks wrong. */
      /* The rows are drawn by renderSplitInto (file scope, shared with llm-fbt.js); this adds the
         buy-box specifics around it. */
      function renderSplit(rows, selRow, choose, names) {
        tilesWrap.classList.add('llm-variant-tiles--split');
        if (pagerEl) { pagerEl.remove(); pagerEl = null; }
        renderSplitInto(tilesWrap, rows, selRow, choose, names, valueMap);
        tilesWrap.scrollLeft = 0;
        updateScrollbar();
      }

      renderTiles = function () {
        if (multiSplit) {
          // One row per variant from the options data; the current one is read from the live selects.
          const cur = optionNames.map(function (o) { return currentSelectedText(o); });
          const mrows = multiData.map(function (v) {
            return { orig: v[1], parts: v[1].map(function (p) { return String(p).trim(); }), oos: !v[2], vid: String(v[0]) };
          });
          const mchoose = function (r) {
            // Every option that differs, in order. Each select fires the theme's change handler, which
            // reads ALL current selections and drops any earlier in-flight fetch, so the last one wins.
            optionNames.forEach(function (o, i) {
              if (currentSelectedText(o) === r.orig[i]) return;
              const fresh = findLiveOption(sectionRoot, o, r.orig[i]);
              if (!fresh) return;
              if (fresh.customSelectEl && typeof fresh.customSelectEl.selectOption === 'function') fresh.customSelectEl.selectOption(fresh.opt);
              else fresh.opt.click();
            });
            updateTrigger();
          };
          const msel = mrows.find(function (r) { return r.orig.every(function (p, i) { return p === cur[i]; }); });
          renderSplit(mrows, msel, mchoose, optionNames);
          return;
        }
        const liveSel = findLiveSelector(sectionRoot, name);
        const values = liveSel
          ? Array.from(liveSel.querySelectorAll('.custom-select__option')).map(optionValueOf)
          : [];
        if (!values.length) return;
        const selNode = liveSel.querySelector('.custom-select__option[aria-selected="true"]');
        const selValue = selNode ? optionValueOf(selNode) : null;
        if (splitWanted(values)) {
          const srows = values.map(function (v) {
            const live = findLiveOption(sectionRoot, name, v);
            return { value: v, parts: splitParts(v), oos: !!(live && live.opt.classList.contains('is-unavailable')),
              vid: live ? live.opt.getAttribute('data-variant-id') : null };
          });
          const schoose = function (r) {
            const fresh = findLiveOption(sectionRoot, name, r.value);
            if (!fresh) return;
            if (fresh.customSelectEl && typeof fresh.customSelectEl.selectOption === 'function') fresh.customSelectEl.selectOption(fresh.opt);
            else fresh.opt.click();
            updateTrigger();
          };
          renderSplit(srows, srows.find(function (r) { return r.value === selValue; }), schoose, null);
          if (!singleSplit) { singleSplit = true; updateTriggerLabel(); }
          return;
        }
        if (singleSplit) { singleSplit = false; updateTriggerLabel(); }
        tilesWrap.classList.remove('llm-variant-tiles--split');

        const paged = tileGridMode();
        let slice, showAll = false, pages = 1, page = 0, shownCount = 0;

        if (paged) {
          /* THREE columns per page, not four. The grid renders FOUR 100px columns but the fourth is
             a faded preview of the next page (Amazon's trick — see llm-variant-tiles.css). So a
             page is 3 x 2 = 6 tiles, which is exactly Amazon's page size and why their 22 variants
             make 4 pages. gridColumns() is deliberately NOT used here: it would report 4 and treat
             the peek column as usable. */
          const pageSize = PAGE_COLS * PAGE_ROWS;
          const needed = Math.ceil(values.length / pageSize);
          const truncated = needed > MAX_PAGES;
          pages = truncated ? MAX_PAGES : needed;
          // One cell of the final page is spent on "Show all" when the list is longer than the cap,
          // so nothing is ever unreachable.
          const capacity = pages * pageSize - (truncated ? 1 : 0);
          shownCount = capacity;          // used by the "+N more options" count below
          const shown = values.slice(0, capacity);
          // The selected variant must always be visible somewhere, even if it sits past the cap.
          if (selValue && shown.indexOf(selValue) === -1) shown[shown.length - 1] = selValue;

          page = tilePage;
          if (page === null) {
            const si = selValue ? shown.indexOf(selValue) : -1;
            page = si >= 0 ? Math.floor(si / pageSize) : 0;   // open where the selection lives
          }
          page = Math.max(0, Math.min(page, pages - 1));
          tilePage = page;

          slice = shown.slice(page * pageSize, page * pageSize + pageSize);
          showAll = truncated && page === pages - 1;
        } else {
          // Scroller (phones, and desktop since 2026-10-26). Desktop shows two rows, so it gets twice
          // the tiles before the "+N more options" card.
          const TILE_CAP = window.matchMedia('(min-width: 1000px)').matches ? 24 : 12;
          const overflow = values.length > TILE_CAP;
          const visibleCount = overflow ? TILE_CAP - 1 : values.length;
          slice = values.slice(0, visibleCount);
          if (overflow && selValue && slice.indexOf(selValue) === -1) slice[slice.length - 1] = selValue;
          showAll = overflow;
        }

        tilesWrap.textContent = '';

        function tileFor(v, isPeek) {
          // Shared tile builder (defined above). on:variant:change re-renders the grid, so the
          // per-tile onSelect only needs to refresh the closed-trigger text.
          const t = buildTile(name, v, function () { updateTrigger(); });
          if (t && isPeek) {
            t.classList.add('llm-variant-tile--peek');
            /* ⚠️ FULLY SELECTABLE — not decoration (owner, 2026-09-21: "why can't a customer select
               the half cards in the 4th column? They should still be able to select those if they
               are shown enough to be able to see what they are").

               He is right and my first version was wrong: I made these `pointer-events: none` and
               aria-hidden on the theory that a half-faded tile is unreadable. But it IS readable —
               the name and price sit in the opaque left portion — and a customer who can read
               "MD (32\" to 44\")" and reaches for it should not find it dead. Showing something
               that looks like a control and refusing the click is worse than either hiding it or
               letting it work.

               Clicking one jumps to the page it belongs to, so the newly selected tile is shown
               fully and in context instead of staying half-faded at the edge. Registered in the
               CAPTURE phase so the page advances before buildTile's own handler triggers the
               variant change and the re-render that follows it. */
            t.addEventListener('click', function () { tilePage = page + 1; }, true);
          }
          return t;
        }

        if (paged) {
          /* ROW-MAJOR WITH THE PEEK INTERLEAVED. The grid is 4 columns and fills left-to-right, so
             to land the page's 6 tiles in columns 1-3 and the preview in column 4 the append order
             has to be:  p0 p1 p2 [peek0]  p3 p4 p5 [peek1]
             Appending the peeks at the END instead would push them onto a third row, where the fade
             would look like a rendering bug rather than a hint.

             ⚠️ Amazon gets this for free because it lays out ALL variants in one wide grid and
             translates it; we render only the current page, so the peek has to be stitched in by
             hand. Keep the interleave and the CSS column count (4) in step. */
          /* ⚠️ EVERY CELL MUST BE APPENDED, INCLUDING THE EMPTY ONES.
             The grid is 4 columns and flows row-major, so a cell left unfilled does not stay
             empty — the next element slides into it. Skipping the append for a missing tile, or
             for the peek on the last page, silently reflows the rest of the page.

             Owner, 2026-09-21: "why is the XL Sample variant card not in the 2nd row like it
             should be?" — on the LAST page there is no next page, so no peek tile was appended,
             and the 4th variant flowed up into row 1 column 4 where the container clipped it in
             half. Row 2 then rendered only two tiles.

             So: build the page's cells first, then walk rows × 4 and append a tile OR an inert
             spacer for every single cell. Position stops depending on how many tiles happen to
             exist. */
          const cells = [];
          slice.forEach(function (v) {
            const t = tileFor(v, false);
            if (t) cells.push(t);
          });
          if (showAll) {
            /* The "+N more options" card is the last CELL of the page, not an extra appended after
               the grid — appended after, it landed on a phantom third row. capacity already
               reserved its slot (`- (truncated ? 1 : 0)`), so it lands in the final column 1-3
               position exactly where a variant would have. */
            cells.push(moreTile('+' + Math.max(0, values.length - shownCount), 'more options', 'Click to view'));
          }

          const peek = values.slice((page + 1) * (PAGE_COLS * PAGE_ROWS));
          let peekIdx = 0;
          for (let row = 0; row < PAGE_ROWS; row++) {
            for (let col = 0; col < PAGE_COLS; col++) {
              const c = cells[row * PAGE_COLS + col];
              tilesWrap.appendChild(c || spacerCell());
            }
            // Column 4: the next page's tile, faded — or a spacer holding the column open.
            const pv = showAll ? undefined : peek[peekIdx];
            const pt = pv === undefined ? null : tileFor(pv, true);
            tilesWrap.appendChild(pt || spacerCell());
            peekIdx++;
          }
        } else {
          slice.forEach(function (v) {
            const t = tileFor(v, false);
            if (t) tilesWrap.appendChild(t);
          });
        }

        if (showAll && !paged) {
          /* ⚠️ SAME CARD IN BOTH MODES. The paged branch briefly rendered this as "Show / all 26",
             which put the word "Show" in the slot the big count belongs in and dropped the
             "Click to view" hint — owner, 2026-09-21: "the show all card looks different than it
             did before, I liked it better before."
             The original three-part shape — big "+N", "more options", red "Click to view" — is what
             tells a customer both HOW MANY are hidden and that the card is clickable. Keep it. */
          const hidden = Math.max(0, values.length - (paged ? shownCount : slice.length));
          tilesWrap.appendChild(moreTile('+' + hidden, 'more options', 'Click to view'));
        }

        if (paged) {
          /* ⚠️ RESET scrollLeft. `overflow: hidden` on the grid makes it a SCROLL CONTAINER — it
             just has no visible scrollbar. The browser quietly scrolls it to bring the selected
             tile into view, which shifted the whole grid left by 2px and clipped the first
             column's left border (owner: "the left hand side of the first card in the top and
             bottom row look like they still have 1px cutoff"). It read like a padding problem and
             was not one — the padding was fine, the content had moved under it.
             The paged grid never scrolls by design, so pinning this to 0 is always correct. */
          tilesWrap.scrollLeft = 0;
          renderPager(pages, page);
          return;                                          // grid: no scroller, no scrollbar
        }

        if (pagerEl) { pagerEl.remove(); pagerEl = null; }
        // Restore the pre-rebuild scroll position (captured live on scroll). Re-assert on the next
        // frame too, since Shopify's variant-change re-render can zero it again just after this runs.
        var _sl = lastScrollLeft;
        tilesWrap.scrollLeft = _sl;
        if (window.requestAnimationFrame) requestAnimationFrame(function () { tilesWrap.scrollLeft = _sl; });
        alignSelectedWhenReady(12);
        updateScrollbar();
      };

      /* TILE_CAP now has THREE bands (12 / 6 / 9), not two, so watching only the 749px boundary
         would leave a stale cap on the 1000px and 1200px crossings — e.g. dragging a window from
         1150 to 1250 would keep the 2-column cap of 6 while CSS switched to 3 columns, rendering
         "+N more" in the middle of a row that had space. Key off the cap itself rather than any one
         media query: it is the value that actually matters, and it cannot drift out of sync with
         the bands above. */
      /* Crossing 1000px swaps the whole layout (paged grid <-> horizontal scroller) AND changes the
         column count, so the current page index is meaningless on the other side. Reset it to null
         so the next render re-opens on the SELECTED variant's page rather than a stale index. */
      var _tileBand = function () {
        return window.matchMedia('(min-width: 1000px)').matches ? 'grid' : 'scroller';
      };
      var _lastTileBand = _tileBand();
      window.addEventListener('resize', function () {
        var b = _tileBand();
        if (b !== _lastTileBand) { _lastTileBand = b; tilePage = null; renderTiles(); }
      });
      renderTiles();
    }

    // The primary in-buy-box element: the inline tile grid when enabled, otherwise the classic
    // closed trigger. Everything below (hide native selectors, re-attach across Shopify's
    // re-renders, insert into the DOM) operates on this so single-option-tiles and the
    // multi/quick-add trigger share the exact same lifecycle handling.
    const primaryAnchor = tilesWrap || trigger;

    // Hiding the CURRENT live option-selectors here is enough for
    // first paint, but not for every re-render after that — Shopify
    // replaces this markup wholesale on every option change, and the
    // fresh replacement nodes won't carry this class. That's fine:
    // llm-option-popup.css also hides
    // `.option-selector[data-selector-type="dropdown"]`
    // unconditionally (not gated behind this class), so every
    // replacement node is hidden automatically without needing to
    // re-apply anything here.
    function hideLiveSelectors() {
      optionNames.forEach(function (optionName) {
        const selector = findLiveSelector(sectionRoot, optionName);
        if (selector) selector.classList.add('llm-option-selector-hidden');
      });
    }
    hideLiveSelectors();

    // Real bug found in testing: the trigger itself (unlike the
    // popup, which lives safely in document.body) was inserted as a
    // sibling of the ORIGINAL option-selector, inside its original
    // parent. Since Shopify replaces that entire parent subtree on
    // every option change, the trigger got discarded along with it —
    // confirmed live: after any variant change, .llm-option-trigger
    // count dropped to 0 and the buy box showed no way to reopen the
    // popup at all, even though the popup itself kept working
    // correctly once opened. Re-anchoring the trigger next to
    // whichever option-selector is CURRENTLY live, every time
    // on:variant:change fires, keeps it permanently visible across
    // however many times Shopify swaps the underlying markup.
    function reattachTrigger() {
      if (primaryAnchor.isConnected) return;
      const liveFirst = findLiveSelector(sectionRoot, optionNames[0]);
      if (liveFirst && liveFirst.parentElement) {
        liveFirst.parentElement.insertBefore(primaryAnchor, liveFirst);
        primaryAnchor.parentElement.insertBefore(triggerLabel, primaryAnchor);
        if (scrollbarEl) { primaryAnchor.insertAdjacentElement('afterend', scrollbarEl); updateScrollbar(); }
      }
    }

    document.addEventListener('on:variant:change', function (evt) {
      const variant = evt.detail && evt.detail.variant;
      if (!variant || !priceMap.hasOwnProperty(variant.id)) return;
      hideLiveSelectors();
      reattachTrigger();
      // Rebuild the inline tiles after Shopify's own variant state settles (no-op when tiles
      // aren't in use). This re-derives which options are visible + their selected state; it reads
      // live option nodes and never re-clicks anything, so it can't loop.
      renderTiles();
    });

    firstOptionSelector.parentElement.insertBefore(primaryAnchor, firstOptionSelector);
    primaryAnchor.parentElement.insertBefore(triggerLabel, primaryAnchor);
    if (scrollbarEl) { primaryAnchor.insertAdjacentElement('afterend', scrollbarEl); updateScrollbar(); }
  }

  function scan() {
    const candidates = document.querySelectorAll('.option-selector[data-selector-type="dropdown"]');
    const groups = new Map(); // sectionRoot -> [optionSelector, ...]
    candidates.forEach(function (sel) {
      const sectionRoot = sel.closest('[id^="shopify-section-"]') || sel.closest('variant-picker') || sel.parentElement;
      if (sectionRoot.dataset.llmPopupBuilt) return;
      if (!groups.has(sectionRoot)) groups.set(sectionRoot, []);
      groups.get(sectionRoot).push(sel);
    });
    groups.forEach(function (selectors, sectionRoot) {
      if (!selectors.length) return;
      const optionNames = selectors.map(function (s) { return s.getAttribute('data-option') || ''; });
      buildGroup(optionNames, sectionRoot, selectors[0]);
    });
  }
  scan();
  new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });
});

/* MAIN PDP PRICE: "($X.XX/ea)" from the same per-unit map the option tiles and the One-time /
   Autoship cards use (owner 2026-10-26: "Should also show the unit price where the main price on
   the PDP is"). price.liquid prints Shopify's variant.unit_price, which is ABSENT on variants with
   no unit-price measurement (MEGAMAX Purple/Black/Pink: no "/ea" at all) and wrong on ~146 packs
   whose quantity_value is 1. #llm-variant-value-data counts from the label instead. Runs on load
   and after each variant change (the price block is re-rendered from the server, so wait for it);
   only writes when the text differs, so it cannot loop. */
(function () {
  function vmap() {
    try { var el = document.getElementById('llm-variant-value-data'); return el ? JSON.parse(el.textContent) : null; }
    catch (e) { return null; }
  }
  function currentVariantId() {
    var q = new URLSearchParams(location.search).get('variant');
    if (q) return q;
    var inp = document.querySelector('.product-info form[action*="/cart/add"] [name="id"], .cc-main-product form[action*="/cart/add"] [name="id"]');
    return inp ? inp.value : null;
  }
  function apply() {
    var m = vmap(), vid = currentVariantId();
    var v = m && vid ? m[vid] : null;
    var text = '';
    if (v && v.per && v.count > 1) text = v.per + '/ea';
    else {
      // Not in the map (samples/trials are left out of it on purpose): price / count-from-name.
      var n = llmCountFromTitle(llmSelectedTitle());
      var pm = null;
      try { var pe = document.getElementById('llm-variant-price-data'); pm = pe ? JSON.parse(pe.textContent) : null; } catch (e) {}
      var cents = pm && vid ? Math.round(parseFloat(String(pm[vid] || '').replace(/[^0-9.]/g, '')) * 100) : 0;
      if (n > 1 && cents > 0) text = '$' + (Math.round(cents / n) / 100).toFixed(2) + '/ea';
    }
    if (!text) return;
    document.querySelectorAll('.cc-main-product .product-info .price, .product-info > .price, .product-info .price').forEach(function (price) {
      if (price.closest('.card, product-card, .llm-autoship, .llm-fbt, quick-add-drawer, .drawer')) return;
      var unit = price.querySelector('.unit-price');
      if (!unit) {
        unit = document.createElement('div');
        unit.className = 'unit-price unit-price--ea relative';
        unit.innerHTML = '<span class="visually-hidden">Unit price</span><span></span>';
        price.appendChild(unit);
      }
      var span = unit.querySelector('span:not(.visually-hidden)') || unit;
      if (span.textContent !== text) span.textContent = text;
      unit.classList.add('unit-price--ea');
    });
  }

  /* Units from the variant NAME, the same first-" of " rule llm-variant-value-data.liquid uses:
     "Trial Pack of 4 *MADE IN USA*" -> 4, "Case of 2,000 (20 boxes of 100)" -> 2000, "100 ct" -> 100.
     Needed because that map deliberately leaves samples/trials OUT (so they can never win Best
     value), and on MEGAMAX's Purple/Pink/Black/Tie-Dye trials Shopify has no unit price either. */
  function llmCountFromTitle(title) {
    var t = String(title || '');
    var m = t.match(/\bof\s+([\d,]+)/i) || t.match(/([\d,]+)\s*(?:ct|count)\b/i);
    var n = m ? Number(m[1].replace(/,/g, '')) : 0;
    return n > 1 ? n : 0;
  }
  function llmSelectedTitle() {
    var o = document.querySelector('.product-info .custom-select__option[aria-selected="true"], .cc-main-product .custom-select__option[aria-selected="true"]');
    return o ? (o.getAttribute('data-value') || o.textContent) : '';
  }
  function soon() { apply(); setTimeout(apply, 250); setTimeout(apply, 900); }
  document.addEventListener('on:variant:change', soon);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', soon);
  else soon();
})();
